/**
 * React bindings of the note lane's panels (W2-03 note-lane): selections over the services' editor store, the open
 * document (version topics), the services swap signal and the lane session. Components re-render only when their
 * selection changes (ARCHITECTURE.md §5.6); nothing here runs per frame.
 */

import { useCallback, useEffect, useState } from 'react';
import type { Unsubscribe } from '../../core/util/types';
import type { ChartDoc, DocIndexes } from '../../core/model/types';
import type { AppServices } from '../../app/registry/types';
import type { DocStore, EditorStore, VersionTopic } from '../../state/types';
import { useExternalSelector } from '../../state/react';
import { laneSession, type LaneSession } from './laneSession';
export { useServicesStore as useStore } from '../../app/registry/react';

/** Selects from the services' editor store. */
export function useEditorSelect<T>(
  services: AppServices,
  select: (s: EditorStore) => T,
  equal?: (a: T, b: T) => boolean,
): T {
  const editor = services.editor;
  const subscribe = useCallback((cb: () => void): Unsubscribe => editor.subscribe(cb), [editor]);
  const get = useCallback(() => editor.getState(), [editor]);
  return useExternalSelector(editor, subscribe, get, select, equal);
}

const NO_STORE = {};
const noop = (): void => undefined;

/** Selects from the open document; re-selects when one of `topics` changed. */
export function useDocSelect<T>(
  store: DocStore | null,
  topics: readonly VersionTopic[],
  select: (doc: ChartDoc, idx: DocIndexes) => T,
  fallback: T,
  equal?: (a: T, b: T) => boolean,
): T {
  const key = topics.join('|');
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      store === null
        ? noop
        : store.subscribe(() => {
            cb();
          }),
    [store],
  );
  const getKey = useCallback(
    (): string =>
      store === null
        ? ''
        : key
            .split('|')
            .map((t) => String(store.version(t as VersionTopic)))
            .join('.'),
    [store, key],
  );
  return useExternalSelector(
    store ?? NO_STORE,
    subscribe,
    getKey,
    () => (store === null ? fallback : select(store.doc, store.idx)),
    equal,
  );
}

/** Re-renders when the lane session emits (tooltip, dialogs, sides). */
export function useSession(services: AppServices): LaneSession {
  const s = laneSession(services);
  const [, setV] = useState(0);
  useEffect(
    () =>
      s.subscribe(() => {
        setV((v) => v + 1);
      }),
    [s],
  );
  return s;
}

/** Shallow equality of arrays (selector results). */
export function sameArray<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
}
